import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LuChevronDown } from "react-icons/lu";
import { cn } from "@/lib/utils";
import { RiGeminiLine, RiOpenaiLine } from "@remixicon/react";

export type Model = "gpt-image-1" | "gpt-image-1.5" | "nano-banana-pro";

export const DEFAULT_MODEL: Model = "gpt-image-1";

export function coerceModel(value: unknown): Model {
  if (typeof value !== "string") return DEFAULT_MODEL;

  // Backward compatibility: older builds stored "gpt" for the image model.
  if (value === "gpt") return "gpt-image-1";

  if (value === "gpt-image-1" || value === "gpt-image-1.5" || value === "nano-banana-pro")
    return value;

  return DEFAULT_MODEL;
}

interface ModelOption {
  value: Model;
  label: string;
  icon: React.ReactNode;
}

interface Props {
  value: Model;
  onChange: (value: Model) => void;
}

const MODEL_OPTIONS: ModelOption[] = [
  {
    value: "gpt-image-1",
    label: "GPT Image 1",
    icon: <RiOpenaiLine size={14} className="shrink-0 text-brand-primary-400" />,
  },
  {
    value: "gpt-image-1.5",
    label: "GPT Image 1.5",
    icon: <RiOpenaiLine size={14} className="shrink-0 text-brand-primary-400" />,
  },
  {
    value: "nano-banana-pro",
    label: "Nano Banana Pro",
    icon: <RiGeminiLine size={14} className="shrink-0 text-brand-primary-400" />,
  },
];

const ModelDropdown: React.FC<Props> = ({ value, onChange }) => {
  const selectedOption =
    MODEL_OPTIONS.find((opt) => opt.value === value) || MODEL_OPTIONS[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center gap-[6px] border-[1px]">
          <div className="text-brand-primary-400 shrink-0">{selectedOption.icon}</div>
          <span className="text-brand-primary-800 text-[12px]">
            {selectedOption.label}
          </span>
          <LuChevronDown className="h-4 w-4 text-brand-primary-new shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="p-1 bg-white shadow-none rounded-[12px] border-0 squircle squircle-radius-[12px] squircle-smooth-[1] squircle-outline-[1px] squircle-outline-color-border-gray"
      >
        {MODEL_OPTIONS.map((opt) => (
          <DropdownMenuItem
            key={opt.value}
            className={cn(
              "flex gap-2 h-[28px] px-2 py-1 items-center self-stretch cursor-pointer text-brand-primary-800 hover:text-brand-primary-800 text-[12px]",
              opt.value === value ? "bg-selected-gray" : "hover:bg-hover-gray",
            )}
            onClick={() => onChange(opt.value)}
          >
            <div className="text-brand-primary-400 shrink-0">{opt.icon}</div>
            <span>{opt.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ModelDropdown;
